import { ValidationArgs, ValidationRule, ValidationRuleWithoutParams, ValidationRuleWithParams } from '@vuelidate/core'
import {
    alpha as alphaImport,
    alphaNum as alphaNumImport,
    and as andImport,
    between as betweenImport,
    decimal as decimalImport,
    email as emailImport,
    integer as integerImport,
    ipAddress as ipAddressImport,
    macAddress as macAddressImport,
    not as notImport,
    numeric as numericImport,
    or as orImport,
    requiredUnless as requiredUnlessImport,
    sameAs as sameAsImport,
    url as urlImport,
    minLength as minLengthImport,
    minValue as minValueImport,
    maxLength as maxLengthImport,
    maxValue as maxValueImport,
    required as requiredImport,
    requiredIf as requiredIfImport,
    helpers as helpersImport,
    MessageProps,
} from '@vuelidate/validators'
import { Ref } from 'vue'
import i18n from '../i18n'

const { t } = i18n.global

export const alpha: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.alpha'), alphaImport)

export const alphaNum: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.alpha-num'), alphaNumImport)

export const and: <T = unknown>(
    ...validators: ValidationRule<T>[]
) => ValidationRuleWithoutParams = andImport

export const between: (
    min: number | Ref<number>,
    max: number | Ref<number>
) => ValidationRuleWithParams = (min, max) => helpersImport.withMessage(({
    $pending,
    $invalid,
    $params,
    $model,
}) => {
    return t('forms.errors.between', { min: $params.min, max: $params.max })
}, betweenImport(min, max))

export const regex: (pattern: any) => ValidationRuleWithoutParams | ValidationRuleWithParams = (pattern) => helpersImport.withMessage(t('forms.errors.regex', { pattern: pattern }), helpersImport.regex(pattern))

export const decimal: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.decimal'), decimalImport)

export const email: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.email'), emailImport)

export const integer: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.integer'), integerImport)

export const ipAddress: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.ip-address'), ipAddressImport)

export const color: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.color'), helpersImport.regex(/#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/))

export const macAddress: (separator: string | Ref<string>) => ValidationRuleWithoutParams | ValidationRuleWithParams =
    (separator) => helpersImport.withMessage(() => {
        return t('forms.errors.mac-address')
    }, macAddressImport(separator))

export const maxLength: (max: number | Ref<number>) => ValidationRuleWithParams =
    (max) => helpersImport.withMessage(({
        $pending,
        $invalid,
        $params,
        $model,
    }) => {
        return t('forms.errors.max-length', { max: $params.max })
    }, maxLengthImport(max))

export const maxValue: (
    max: number | Ref<number> | string | Ref<string>) => ValidationRuleWithParams = (max) => helpersImport.withMessage(({
        $pending,
        $invalid,
        $params,
        $model,
    }) => {
        return t('forms.errors.max-value', { max: $params.max })
    }, maxValueImport(max))

export const minLength: (min: number | Ref<number>) => ValidationRuleWithParams =
    (min) => helpersImport.withMessage(({
        $pending,
        $invalid,
        $params,
        $model,
    }) => {
        return t('forms.errors.min-length', { min: $params.min })
    }, minLengthImport(min))

export const minValue: (
    min: number | Ref<number> | string | Ref<string>) => ValidationRuleWithParams = (min) => helpersImport.withMessage(({
        $pending,
        $invalid,
        $params,
        $model,
    }) => {
        return t('forms.errors.min-value', { min: $params.min })
    }, minValueImport(min))

export const not: <T = unknown>(validator: ValidationRule<T>) => ValidationRuleWithoutParams | ValidationRuleWithParams = validator =>
    helpersImport.withMessage(t('forms.errors.not'), notImport(validator))

export const numeric: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.numeric'), numericImport)

export const or: <T = unknown>(...validators: ValidationRule<T>[]) => ValidationRuleWithoutParams = orImport

export const required: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.required'), requiredImport)

export const requiredIf: (prop: boolean | Ref<boolean> | string | (() => boolean | Promise<boolean>)) => ValidationRuleWithoutParams | ValidationRuleWithParams =
    (prop) => helpersImport.withMessage(() => {
        return t('forms.errors.required')
    }, requiredIfImport(prop))

export const requiredUnless: (prop: boolean | Ref<boolean> | string | (() => boolean | Promise<boolean>)) => ValidationRuleWithoutParams | ValidationRuleWithParams =
    (prop) => helpersImport.withMessage(() => {
        return t('forms.errors.required')
    }, requiredUnlessImport(prop))

export const sameAs: <E = unknown>(equalTo: E | Ref<E>, otherName?: string) => ValidationRuleWithParams =
    (equalTo, otherName) => helpersImport.withMessage(t('forms.errors.same-as'), sameAsImport(equalTo, otherName))

export const url: ValidationRuleWithoutParams | ValidationRuleWithParams = helpersImport.withMessage(t('forms.errors.url'), urlImport)

export const helpers: {
    withParams: <T = unknown>(params: object, validator: ValidationRule<T>) => ValidationRuleWithParams
    withMessage: <T = unknown>(message: string | ((params: MessageProps) => string), validator: ValidationRule<T>) => ValidationRuleWithParams
    req: Function
    len: Function
    regex: Function
    unwrap: Function
    withAsync: Function,
    forEach: (validators: ValidationArgs) => { $validator: ValidationRule, $message: () => string }
} = helpersImport

export const unique = (param: Array<any>) => helpers.withMessage(t('forms.errors.unique'), uniqueCheck(param))

function uniqueCheck(param: Array<any>) {
    return helpers.withParams(
        { type: 'array', value: param },
        value => !helpers.req(value) || param.filter(element => element === value).length === 1,
    )
}